import { useEffect } from "react";
import cloneDeep from "lodash-es/cloneDeep";
import { useQueryClient } from "@tanstack/react-query";
import { useFactoryWashout } from "../useFactoryWashout";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { getDepartmentalPosition, isProducerPosition } from "@/Common/helpers/Positions";

interface ResultType {
	[key: string]: {
		_id: string;
		employee: {
			_id: string;
		};
		summary: unknown;
	};
}

const useWSSubscriptions = ({
	payPeriodId = "",
	position = "",
	userId = "",
	commissionCycleId = "",
	dependencies = [],
	onUpdateCallback = () => {},
}: {
	payPeriodId: string;
	position: string;
	userId: string;
	commissionCycleId: string;
	dependencies?: unknown[];
	onCreateCallback?: () => void;
	onUpdateCallback?: (result: { data: ResultType }) => void;
}) => {
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();
	const { useWashoutSubscriptions } = useFactoryWashout();
	const positionToUse = getDepartmentalPosition(position);
	const isDepartmental = isProducerPosition(position) ? "Producer" : "Departmental";
	const subscribeWashout = useWashoutSubscriptions(positionToUse, isDepartmental);
	const onCreateWashout = subscribeWashout?.get("onCreateWashout");
	const onUpdateWashout = subscribeWashout?.get("onUpdateWashout");

	const onCreateGetter = onCreateWashout?.getter ?? "";
	const onUpdateGetter = onUpdateWashout?.getter ?? "";
	const onCreateQuery = (onCreateWashout?.query ? (onCreateWashout?.query as unknown as Function) : () => "")?.();
	const onUpdateQuery = (onUpdateWashout?.query ? (onUpdateWashout?.query as unknown as Function) : () => "")?.();

	const washoutSubscription = getSubscription(["useWSSubscriptions", payPeriodId, position, userId, commissionCycleId]);

	washoutSubscription?.subscriptions?.[onCreateGetter]?.setCallback(() => {
		return queryClient.refetchQueries({ queryKey: ["getWashout", payPeriodId, position, userId, commissionCycleId] });
	});

	washoutSubscription?.subscriptions?.[onUpdateGetter]?.setCallback((result: { data: ResultType }) => {
		const washoutUpdated = result?.data?.[onUpdateGetter];
		queryClient?.setQueriesData?.(
			{ queryKey: ["getWashout", payPeriodId, position, userId, commissionCycleId] },
			(oldData?: { _id: string; summary: unknown }) => {
				const newData = cloneDeep(oldData);
				if (newData?._id === washoutUpdated?._id) {
					newData.summary = washoutUpdated.summary;
				}
				return newData;
			}
		);
		onUpdateCallback?.(result);
	});

	useEffect(() => {
		if (!onCreateWashout?.query || !onUpdateWashout?.query) {
			return;
		}

		addSubscription(
			["useWSSubscriptions", payPeriodId, position, userId, commissionCycleId],
			[
				{
					context: onCreateGetter,
					query: onCreateQuery,
					variables: {
						payPeriodId,
						position,
						userId,
						commissionCycleId,
					},
				},
				{
					context: onUpdateGetter,
					query: onUpdateQuery,
					variables: {
						payPeriodId,
						position,
						userId,
						commissionCycleId,
					},
				},
			]
		);
	}, dependencies);
};

export default useWSSubscriptions;
